export const CATEGORIES = [
    { value: 'CORE', label: 'Core Feature', token: '--visual-color-1' },
    { value: 'META', label: 'Meta System', token: '--visual-color-2' },
    { value: 'LIVEOPS', label: 'LiveOps', token: '--visual-color-4' },
    { value: 'ECONOMY', label: 'Economy', token: '--visual-color-8' },
    { value: 'SOCIAL', label: 'Social', token: '--visual-color-6' },
    { value: 'COMPETITIVE', label: 'Competitive', token: '--visual-color-3' },
    { value: 'TECHNICAL', label: 'Technical', token: '--text-3' },
    { value: 'OTHER', label: 'Other', token: '--text-4' },
];

export const categoryMeta = (value) => CATEGORIES.find((c) => c.value === value) || CATEGORIES[CATEGORIES.length - 1];

export const OWNERS = [
    { value: 'GAME_SERVER', label: 'Game server', tone: 'neutral' },
    { value: 'GEOPLAY', label: 'GeoPlay', tone: 'primary' },
    { value: 'SHARED', label: 'Shared', tone: 'violet' },
];

export const ownerMeta = (value) => OWNERS.find((o) => o.value === value) || OWNERS[1];

export const KNOWLEDGE_KINDS = [
    { key: 'AUDIENCE', label: 'Audience', token: '--visual-color-2' },
    { key: 'GAMEPLAY', label: 'Gameplay', token: '--visual-color-7' },
    { key: 'PROGRESSION', label: 'Progression', token: '--visual-color-6' },
    { key: 'MONETIZATION', label: 'Monetization', token: '--visual-color-8' },
    { key: 'CONTENT', label: 'Content', token: '--visual-color-4' },
    { key: 'LIVEOPS', label: 'LiveOps', token: '--danger' },
    { key: 'ACQUISITION', label: 'Acquisition', token: '--visual-color-10' },
    { key: 'TECHNICAL', label: 'Technical', token: '--text-3' },
    { key: 'GENERAL', label: 'General', token: '--text-4' },
];

export const knowledgeKindMeta = (key) => KNOWLEDGE_KINDS.find((k) => k.key === key) || KNOWLEDGE_KINDS[KNOWLEDGE_KINDS.length - 1];

// What a card is, beside its topic. A snippet stored before types existed has none and reads as the first.
export const SNIPPET_TYPES = [
    { key: 'KNOWLEDGE_SNIPPET', label: 'Knowledge snippet' },
    { key: 'EVENT_RULE', label: 'Event rule' },
    { key: 'NOTE', label: 'Note' },
];

export const snippetTypeMeta = (key) => SNIPPET_TYPES.find((t) => t.key === key) || SNIPPET_TYPES[0];

// Snippets and documents say where they belong: this system plus the event's id, or no id when every event shares them.
export const EVENT_SYSTEM = 'LIVE_EVENT';

const CADENCE_GROUPS = [
    { key: 'daily', label: 'Daily and faster', match: (e) => e.scheduleKind !== 'ONE_TIME' && ['MINUTE', 'HOUR', 'DAY'].includes(e.intervalUnit) },
    { key: 'weekly', label: 'Weekly', match: (e) => e.scheduleKind !== 'ONE_TIME' && e.intervalUnit === 'WEEK' },
    { key: 'monthly', label: 'Monthly and longer', match: (e) => e.scheduleKind !== 'ONE_TIME' && ['MONTH', 'QUARTER', 'YEAR'].includes(e.intervalUnit) },
    { key: 'once', label: 'One-off', match: (e) => e.scheduleKind === 'ONE_TIME' },
];

export const LIST_GROUPS = [
    ...CADENCE_GROUPS,
    // A new event has no schedule until someone sets one. It waits here, with anything else without a cadence.
    { key: 'unscheduled', label: 'No schedule yet', match: (e) => !CADENCE_GROUPS.some((g) => g.match(e)) },
];

export const LINK_TYPES = [
    { value: 'CONFIG', label: 'Config', plural: 'Configs' },
    { value: 'KNOWLEDGE', label: 'Knowledge', plural: 'Knowledge' },
    { value: 'EVENT', label: 'Live Event', plural: 'Live Events' },
    { value: 'OBJECTIVE', label: 'Objective', plural: 'Objectives' },
];

export const TASK_LINK_TYPES = LINK_TYPES.filter((t) => t.value === 'CONFIG' || t.value === 'KNOWLEDGE');

// Keyed by a task result's status; ANSWERED is a COMPLETED result with nothing to change.
export const RESULT_META = {
    COMPLETED: { label: 'Runs', tone: 'success' },
    ANSWERED: { label: 'Answered', tone: 'neutral' },
    MANUAL: { label: 'Manual', tone: 'neutral' },
    NEEDS_INPUT: { label: 'Needs detail', tone: 'warning' },
    NOT_ACTIONABLE: { label: 'Unclear', tone: 'neutral' },
    FAILED: { label: 'Couldn’t finish', tone: 'warning' },
};

export const VERDICT_LABEL = {
    HIT: 'Hit',
    PARTIAL: 'Partial',
    MISSED: 'Missed',
    HELD: 'Held',
    DRIFTED: 'Drifted',
    INCONCLUSIVE: 'Inconclusive',
    NOT_MEASURED: 'Not measured',
};

export const verdictTone = (v) => {
    if (v === 'HIT' || v === 'HELD') return 'success';
    if (v === 'PARTIAL') return 'warning';
    if (v === 'MISSED' || v === 'DRIFTED') return 'danger';
    return 'neutral';
};

export const HISTORY_PERIODS = [
    { key: '5r', label: 'Last 5 runs', runs: 5 },
    { key: '10r', label: 'Last 10 runs', runs: 10 },
    { key: '3m', label: '3 months', months: 3 },
    { key: '6m', label: '6 months', months: 6 },
    { key: '12m', label: '12 months', months: 12 },
    { key: 'all', label: 'All' },
];

export const RULE_OPS = [
    { value: 'MAX', label: 'is never above', needs: 'number' },
    { value: 'MIN', label: 'is never below', needs: 'number' },
    { value: 'ONE_OF', label: 'is one of', needs: 'list' },
    { value: 'NEVER_EMPTY', label: 'is never empty', needs: 'none' },
];

export const runRoleLabel = (role) => (role === 'INPUT' ? 'Inputs & configs' : 'Measured results');

export const MAX_UPLOAD_MB = 10;

// An event document goes to Game Knowledge as one JSON body: the file base64-encoded (4 bytes for every 3) and its
// text beside it. geo-play-web decodes that body in memory and sets no `spring.codec.max-in-memory-size`, so Spring's
// default of 256 KB is the cap, and it is the request that has to fit, not the file. Raise this with that property.
export const MAX_DOCUMENT_REQUEST_BYTES = 256 * 1024;

// The largest file that can fit, before its text is counted: the rest of the request is well under 2 KB.
export const MAX_DOCUMENT_BYTES = Math.floor(((MAX_DOCUMENT_REQUEST_BYTES - 2048) * 3) / 4);

export const formatBytes = (n) => {
    if (!n) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatDocBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
    return `${Math.round((n / 1024 / 1024) * 10) / 10} MB`;
};

const FIXED_UTC = { timeZone: 'UTC' };

export const fmtRunDate = (ms) => new Date(ms).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', ...FIXED_UTC,
});

export const fmtWeekdayDate = (ms) => new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...FIXED_UTC,
}).format(new Date(ms));

export const fmtDay = (ms) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...FIXED_UTC })
    .format(new Date(ms));

export const fmtPct = (n) => `${n > 0 ? '+' : ''}${Math.round(n * 10) / 10}%`;

export const fmtNum = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
